import {useCallback} from 'react';
import useUpdatePayinSessionMutation from 'src/api/graphql/useUpdatePayinSessionMutation';
import useUpdateSetupSessionMutation from 'src/api/graphql/useUpdateSetupSessionMutation';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {convertWalletTypeToApiType} from 'src/utils/convertWalletTypeToApiType';
import {getStore} from 'src/v2/state/store';
import {convertGraphQlToWalletSession} from 'src/v2/util/convertGraphQlToStoreState';
import type {Wallet} from 'src/v2/state/types';
import {logger} from 'src/utils/logger';

export const useDisconnectWallet = (): ((
  activeWallet: Wallet,
) => Promise<void>) => {
  const store = getStore();
  const sessionContext = useSessionContext();
  const walletClient = useWalletConnectionClient();
  const [updatePayinSessionMutation] = useUpdatePayinSessionMutation();
  const [updateSetupSessionMutation] = useUpdateSetupSessionMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);
  const connectedWallets = store.useSelector(
    (state) => state.walletConnection.connectedWallets,
  );

  return useCallback(
    async (activeWallet: Wallet): Promise<void> => {
      if (!activeWallet) {
        return;
      }

      const screen =
        connectedWallets.length > 1 ? 'WALLET_SWITCH' : 'WALLET_SELECT';

      logger.info('disconnecting wallet', {
        provider: activeWallet.type,
        network: activeWallet.network,
        number_connected_wallets: connectedWallets.length,
      });

      try {
        //  Call the API first so state.walletConnection.activeWallet is not null for long time
        const walletParams = {
          wallet_address: activeWallet.address,
          network: activeWallet.network,
          provider: convertWalletTypeToApiType(activeWallet.type),
          connected: false,
          active: false,
          type: convertWalletTypeToApiType(activeWallet.type),
          wallet_key: activeWallet.key,
        };

        let updatedSession;

        if (sessionContext.mode === 'pay') {
          const response = await updatePayinSessionMutation({
            variables: {
              input: {
                client_secret: clientSecret,
                crypto_payin_session: sessionId,
                wallet_params: [walletParams],
              },
            },
          });

          updatedSession = response.data?.v1CryptoInternalPayinSessionUpdate;
        } else {
          const response = await updateSetupSessionMutation({
            variables: {
              input: {
                client_secret: clientSecret,
                crypto_setup_session: sessionId,
                wallet_params: [walletParams],
              },
            },
          });

          updatedSession =
            response.data?.v1CryptoInternalSetupSessionCryptoSetupSessionUpdate;
        }

        await walletClient?.disconnectWallet(activeWallet.id);

        store.setValue((state) => {
          state.screen = screen;
          // Eagerly update state ahead of async wallet client callback to prevent flash
          state.walletConnection.connectedWallets =
            state.walletConnection.connectedWallets.filter(
              (w) => w.id !== activeWallet.id,
            );
          if (state.walletConnection.activeWallet?.id === activeWallet.id) {
            state.walletConnection.activeWallet =
              state.walletConnection.connectedWallets[0] ?? null;
          }

          if (updatedSession) {
            state.state = updatedSession.state;
            state.walletSessions = updatedSession.wallet_sessions.map(
              convertGraphQlToWalletSession,
            );
          }
        });
      } catch (error) {
        // TODO: handle error - it's probably okay if this API call fails intermittently.
        // We'll call /update again later anyway
        store.setValue((state) => {
          state.screen = screen;
        });
        logger.log('crypto_payin_session.error', {
          error_message: error instanceof Error ? error.message : String(error),
          error_context: 'use_disconnect_wallet',
        });
      }
    },
    [
      sessionId,
      clientSecret,
      sessionContext.mode,
      walletClient,
      updatePayinSessionMutation,
      updateSetupSessionMutation,
      store,
      connectedWallets.length,
    ],
  );
};
